import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import type { TFunction } from 'i18next';
import { X } from 'lucide-react';
import { DataTable } from '@/components/shared/DataTable';
import { inventoryService } from '@/services/inventoryService';
import { supplyService } from '@/services/supplyService';
import type { ApiError } from '@/services/api';
import { ReceivePurchaseOrderDialog } from '@/components/shared/ReceivePurchaseOrderDialog';
import type {
  OrderHealthStatus,
  OrderTrackingOrder,
  OrderTrackingOrderItem,
  OrderTrackingOrderMetrics,
  ReceptionStatus,
} from '@/services/orderTracking/orderTracking.types';

type EnrichedOrder = OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics };
type LineFilter = 'all' | 'pending' | 'late';

const SIDEBAR_ANIMATION_MS = 300;
const SIDEBAR_WIDTH_KEY = 'orderTracking.sidebarWidthPct';
const DEFAULT_WIDTH_PCT = 58;
const MIN_WIDTH_PCT = 32;
const MAX_WIDTH_PCT = 92;

function clampWidthPct(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_WIDTH_PCT;
  return Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, Math.round(value)));
}

function readInitialWidthPct(): number {
  try {
    const raw = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (raw == null || raw === '') return DEFAULT_WIDTH_PCT;
    return clampWidthPct(Number(raw));
  } catch {
    return DEFAULT_WIDTH_PCT;
  }
}

export interface OrderTrackingDetailSidebarProps {
  order: EnrichedOrder;
  items: OrderTrackingOrderItem[];
  onClose: () => void;
  onReceived?: () => void;
}

function normalizeHealthStatus(status: OrderHealthStatus): OrderHealthStatus {
  return status === 'partial' ? 'on_time' : status;
}

function getOrderHealthLabel(status: OrderHealthStatus, t: TFunction): string {
  return t(`orderTracking.healthStatus.${normalizeHealthStatus(status)}`);
}

function getOrderHealthBadgeClasses(status: OrderHealthStatus): string {
  const s = normalizeHealthStatus(status);
  if (s === 'critical') return 'bg-red-100 text-red-800 border-red-200';
  if (s === 'late') return 'bg-red-50 text-red-700 border-red-200';
  if (s === 'at_risk') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status === 'received') return 'bg-green-50 text-green-700 border-green-200';
  if (status === 'cancelled') return 'bg-gray-100 text-gray-700 border-gray-300';
  return 'bg-green-50 text-green-700 border-green-200';
}

function getReceptionLabel(status: ReceptionStatus, t: TFunction): string {
  return t(`orderTracking.reception.${status}`);
}

function getReceptionBadgeClasses(status: ReceptionStatus): string {
  if (status === 'late') return 'bg-red-50 text-red-700 border-red-200';
  if (status === 'at_risk') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-green-50 text-green-700 border-green-200';
}

function formatDisplayDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  const parsed = new Date(value.includes('T') ? value : `${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(locale);
}

function getLineExpectedDate(item: OrderTrackingOrderItem, order: EnrichedOrder): string | null {
  return item.expectedReceivedDate ?? order.expectedDate ?? order.effectiveDeadline ?? null;
}

function getLineReceivedDate(item: OrderTrackingOrderItem, order: EnrichedOrder): string | null {
  if (item.receivedDate) return item.receivedDate;
  if (item.receivedQuantity > 0 && order.receivedDate) return order.receivedDate;
  return null;
}

function getOrderDisplayReceivedDate(order: EnrichedOrder, items: OrderTrackingOrderItem[]): string | null {
  if (order.receivedDate) return order.receivedDate;
  const lineDates = items
    .map((item) => getLineReceivedDate(item, order))
    .filter((d): d is string => Boolean(d));
  if (lineDates.length === 0) return null;
  return lineDates.sort((a, b) => b.localeCompare(a))[0];
}

function linePendingValue(item: OrderTrackingOrderItem): number {
  const qty = item.quantity;
  if (qty <= 0) return 0;
  const pending = item.pendingQuantity;
  if (pending <= 0) return 0;
  return item.totalPrice * (pending / qty);
}

function isLineOpen(item: OrderTrackingOrderItem): boolean {
  return item.pendingQuantity > 0 && item.status !== 'CANCELLED';
}

function computeSidebarSummary(items: OrderTrackingOrderItem[], metrics: OrderTrackingOrderMetrics) {
  const quantityOrdered = items.reduce((acc, i) => acc + i.quantity, 0);
  const quantityReceived = items.reduce((acc, i) => acc + i.receivedQuantity, 0);
  const quantityPending = items.reduce((acc, i) => acc + i.pendingQuantity, 0);
  const pendingValue = items.reduce((acc, i) => acc + linePendingValue(i), 0);
  const linesPending = items.filter(isLineOpen).length;
  const progressUnits =
    metrics.progressUnits ??
    metrics.progress ??
    (quantityOrdered > 0 ? Math.round((quantityReceived / quantityOrdered) * 100) : 0);

  return {
    quantityPending,
    linesPending,
    pendingValue,
    progressUnits,
  };
}

function KpiTile({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: 'danger' | 'default';
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={`mt-0.5 text-lg font-semibold tabular-nums ${
          emphasize === 'danger' ? 'text-red-700' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

export function OrderTrackingDetailSidebar({ order, items, onClose, onReceived }: OrderTrackingDetailSidebarProps) {
  const { t, i18n } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const locale = i18n.language;
  const fmt = (n: number) => n.toLocaleString(locale);
  const [isAnimating, setIsAnimating] = useState(false);
  const [widthPct, setWidthPct] = useState(readInitialWidthPct);
  const [lineFilter, setLineFilter] = useState<LineFilter>('all');
  const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(() => new Set());
  const [receiving, setReceiving] = useState(false);
  const [receiveMode, setReceiveMode] = useState<'selected' | 'order' | 'line' | null>(null);
  const [receiveLineId, setReceiveLineId] = useState<number | null>(null);
  const [receiveError, setReceiveError] = useState<string | null>(null);
  const closingRef = useRef(false);
  const resizingRef = useRef(false);

  useLayoutEffect(() => {
    const timer = window.setTimeout(() => setIsAnimating(true), 10);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(widthPct));
    } catch {
      /* ignore */
    }
  }, [widthPct]);

  const onResizePointerMove = useCallback((e: PointerEvent) => {
    if (!resizingRef.current) return;
    const vw = window.innerWidth || 1;
    const next = ((vw - e.clientX) / vw) * 100;
    setWidthPct(clampWidthPct(next));
  }, []);

  const stopResize = useCallback(() => {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onResizePointerMove);
    window.removeEventListener('pointerup', stopResize);
  }, [onResizePointerMove]);

  const startResize = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onResizePointerMove);
      window.addEventListener('pointerup', stopResize);
    },
    [onResizePointerMove, stopResize],
  );

  useEffect(() => () => stopResize(), [stopResize]);

  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setIsAnimating(false);
    window.setTimeout(() => {
      onClose();
    }, SIDEBAR_ANIMATION_MS);
  }, [onClose]);

  const summary = useMemo(() => computeSidebarSummary(items, order.metrics), [items, order.metrics]);
  const showReceivedDate = Boolean(getOrderDisplayReceivedDate(order, items));
  const hasLinesWithSo = items.some((i) => i.salesOrderId !== null);

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const aOpen = isLineOpen(a) ? 0 : 1;
        const bOpen = isLineOpen(b) ? 0 : 1;
        if (aOpen !== bOpen) return aOpen - bOpen;
        const aLate = a.receptionStatus === 'late' ? 0 : 1;
        const bLate = b.receptionStatus === 'late' ? 0 : 1;
        if (aLate !== bLate) return aLate - bLate;
        return a.productName.localeCompare(b.productName);
      }),
    [items]
  );

  const filteredItems = useMemo(() => {
    if (lineFilter === 'pending') return sortedItems.filter(isLineOpen);
    if (lineFilter === 'late') return sortedItems.filter((i) => i.receptionStatus === 'late');
    return sortedItems;
  }, [sortedItems, lineFilter]);

  const openLineIds = useMemo(
    () => filteredItems.filter(isLineOpen).map((i) => i.id),
    [filteredItems]
  );

  useEffect(() => {
    setSelectedLineIds(new Set());
  }, [order.id]);

  const toggleLineSelected = useCallback((lineId: number, open: boolean) => {
    if (!open) return;
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }, []);

  const toggleSelectAllOpen = useCallback(() => {
    setSelectedLineIds((prev) => {
      const allSelected = openLineIds.length > 0 && openLineIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(openLineIds);
    });
  }, [openLineIds]);

  const afterReceive = useCallback(
    async (result: {
      product_ids?: number[];
      supplier_id?: number;
      supplier_ids?: number[];
    }) => {
      const supplierIds = new Set<number>();
      if (typeof result.supplier_id === 'number') supplierIds.add(result.supplier_id);
      for (const sid of result.supplier_ids ?? []) {
        if (typeof sid === 'number') supplierIds.add(sid);
      }
      try {
        if (supplierIds.size > 0) {
          await Promise.all(
            [...supplierIds].map((sid) =>
              supplyService.recalculateSupplier(sid, { force: true, skip_bom: false })
            )
          );
        } else {
          const productIds = [...new Set(result.product_ids ?? [])];
          await Promise.all(
            productIds.map((pid) =>
              supplyService.recalculateProduct(pid, { force: true, skip_bom: false })
            )
          );
        }
      } catch (error) {
        console.error('Error recalculating after mark-received:', error);
      }
      onReceived?.();
    },
    [onReceived]
  );

  const handleMarkSelectedReceived = useCallback(async (payload: {
    received_date: string;
    received_quantity: number | null;
  }) => {
    const ids = [...selectedLineIds];
    if (ids.length === 0 || receiving) return;
    setReceiving(true);
    setReceiveError(null);
    try {
      const result = await inventoryService.markPurchaseOrderItemsReceived(ids, {
        received_date: payload.received_date,
        received_quantity: ids.length === 1 ? payload.received_quantity : null,
      });
      setSelectedLineIds(new Set());
      setReceiveMode(null);
      setReceiveLineId(null);
      await afterReceive(result);
    } catch (error) {
      console.error('Error marking selected PO lines as received:', error);
      const apiError = error as ApiError;
      setReceiveError(apiError.message || t('errors.serverError'));
    } finally {
      setReceiving(false);
    }
  }, [selectedLineIds, receiving, afterReceive, t]);

  const handleMarkOrderReceived = useCallback(async (payload: {
    received_date: string;
  }) => {
    if (receiving || summary.linesPending === 0) return;
    setReceiving(true);
    setReceiveError(null);
    try {
      const result = await inventoryService.markPurchaseOrderReceived(order.id, {
        received_date: payload.received_date,
      });
      setSelectedLineIds(new Set());
      setReceiveMode(null);
      setReceiveLineId(null);
      await afterReceive(result);
    } catch (error) {
      console.error('Error marking purchase order as received:', error);
      const apiError = error as ApiError;
      setReceiveError(apiError.message || t('errors.serverError'));
    } finally {
      setReceiving(false);
    }
  }, [receiving, summary.linesPending, order.id, afterReceive, t]);

  const handleMarkLineReceived = useCallback(async (payload: {
    received_date: string;
    received_quantity: number | null;
  }) => {
    if (receiveLineId == null || receiving) return;
    setReceiving(true);
    setReceiveError(null);
    try {
      const result = await inventoryService.markPurchaseOrderItemReceived(receiveLineId, {
        received_date: payload.received_date,
        received_quantity: payload.received_quantity,
      });
      setSelectedLineIds((prev) => {
        const next = new Set(prev);
        next.delete(receiveLineId);
        return next;
      });
      setReceiveMode(null);
      setReceiveLineId(null);
      await afterReceive(result);
    } catch (error) {
      console.error('Error marking PO line as received:', error);
      const apiError = error as ApiError;
      setReceiveError(apiError.message || t('errors.serverError'));
    } finally {
      setReceiving(false);
    }
  }, [receiveLineId, receiving, afterReceive, t]);

  const handleCancelOrder = useCallback(async () => {
    if (receiving || summary.linesPending === 0) return;
    const selectedIds = [...selectedLineIds].filter((id) => openLineIds.includes(id));
    const cancelSelected = selectedIds.length > 0;
    const ok = window.confirm(
      cancelSelected
        ? t('orderTracking.sidebar.cancelSelectedConfirm', { count: selectedIds.length })
        : t('orderTracking.sidebar.cancelOrderConfirm')
    );
    if (!ok) return;
    setReceiving(true);
    setReceiveError(null);
    try {
      const result = cancelSelected
        ? await inventoryService.cancelPurchaseOrderItems(selectedIds)
        : await inventoryService.cancelPurchaseOrder(order.id);
      setSelectedLineIds(new Set());
      setReceiveMode(null);
      setReceiveLineId(null);
      await afterReceive(result);
    } catch (error) {
      console.error('Error cancelling purchase order / lines:', error);
      const apiError = error as ApiError;
      setReceiveError(apiError.message || t('errors.serverError'));
    } finally {
      setReceiving(false);
    }
  }, [receiving, summary.linesPending, selectedLineIds, openLineIds, order.id, afterReceive, t]);

  const openLineReceive = useCallback((item: OrderTrackingOrderItem) => {
    if (!isLineOpen(item) || receiving) return;
    setReceiveError(null);
    setReceiveLineId(item.id);
    setReceiveMode('line');
  }, [receiving]);

  const allOpenSelected =
    openLineIds.length > 0 && openLineIds.every((id) => selectedLineIds.has(id));

  const itemColumns = useMemo(() => {
    const cols = [
      {
        header: (
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-400 text-sky-600 focus:ring-sky-500"
            checked={allOpenSelected}
            disabled={openLineIds.length === 0 || receiving}
            onChange={toggleSelectAllOpen}
            aria-label={t('orderTracking.sidebar.selectAllOpenLines')}
            title={t('orderTracking.sidebar.selectAllOpenLines')}
          />
        ),
        accessor: 'id' as const,
        width: '44px',
        exportHeader: t('orderTracking.sidebar.selectLine'),
        cell: (_: number, item: OrderTrackingOrderItem) => {
          const open = isLineOpen(item);
          return (
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-400 text-sky-600 focus:ring-sky-500 disabled:opacity-40"
              checked={open && selectedLineIds.has(item.id)}
              disabled={!open || receiving}
              onChange={() => toggleLineSelected(item.id, open)}
              aria-label={t('orderTracking.sidebar.selectLine')}
              title={t('orderTracking.sidebar.selectLine')}
            />
          );
        },
      },
      {
        header: t('orderTracking.table.items.product'),
        accessor: 'productName' as const,
        width: '180px',
        cell: (v: string, i: OrderTrackingOrderItem) => (
          <div className="min-w-0 max-w-[180px]">
            <p className="text-gray-900 font-medium truncate" title={v}>
              {v}
            </p>
            <p className="text-xs text-gray-500 font-mono truncate" title={i.productCode}>
              {i.productCode}
            </p>
          </div>
        ),
      },
      {
        header: t('orderTracking.table.items.expectedDate'),
        accessor: 'expectedReceivedDate' as const,
        width: '82px',
        cell: (_: unknown, item: OrderTrackingOrderItem) => (
          <span className="text-gray-700 whitespace-nowrap tabular-nums">
            {formatDisplayDate(getLineExpectedDate(item, order), locale)}
          </span>
        ),
      },
      ...(showReceivedDate
        ? [
            {
              header: t('orderTracking.table.items.receivedDate'),
              accessor: 'receivedDate' as const,
              width: '82px',
              cell: (_: unknown, item: OrderTrackingOrderItem) => (
                <span className="text-gray-700 whitespace-nowrap tabular-nums">
                  {formatDisplayDate(getLineReceivedDate(item, order), locale)}
                </span>
              ),
            },
          ]
        : []),
      {
        header: t('orderTracking.table.items.qtyOrdered'),
        accessor: 'quantity' as const,
        width: '78px',
        cell: (v: number) => (
          <span className="tabular-nums whitespace-nowrap text-gray-700">{v.toLocaleString(locale)}</span>
        ),
      },
      {
        header: t('orderTracking.table.items.qtyReceived'),
        accessor: 'receivedQuantity' as const,
        width: '78px',
        cell: (v: number) => (
          <span className="tabular-nums whitespace-nowrap text-gray-700">{v.toLocaleString(locale)}</span>
        ),
      },
      {
        header: t('orderTracking.table.items.qtyPending'),
        accessor: 'pendingQuantity' as const,
        width: '78px',
        cell: (v: number) => (
          <span
            className={`tabular-nums whitespace-nowrap ${v > 0 ? 'font-medium text-amber-800' : 'text-gray-500'}`}
          >
            {v.toLocaleString(locale)}
          </span>
        ),
      },
      {
        header: t('orderTracking.table.items.reception'),
        accessor: 'receptionStatus' as const,
        width: '88px',
        cell: (_: unknown, i: OrderTrackingOrderItem) => {
          const st = i.receptionStatus ?? 'on_time';
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs border whitespace-nowrap ${getReceptionBadgeClasses(st)}`}
            >
              {getReceptionLabel(st, t)}
            </span>
          );
        },
      },
      ...(hasLinesWithSo
        ? [
            {
              header: t('orderTracking.table.items.salesOrder'),
              accessor: 'salesOrderNumber' as const,
              width: '72px',
              cell: (v: string | null) => (
                <span className="text-gray-500 whitespace-nowrap">{v ?? '—'}</span>
              ),
            },
          ]
        : []),
      {
        header: t('orderTracking.table.items.lineTotal'),
        accessor: 'totalPrice' as const,
        width: '88px',
        cell: (v: number) => (
          <span className="tabular-nums whitespace-nowrap">{formatMoney(v)}</span>
        ),
      },
      {
        header: t('orderTracking.table.items.receiveAction'),
        accessor: 'receiveAction' as const,
        width: '92px',
        exportHeader: t('orderTracking.table.items.receiveAction'),
        cell: (_: number, item: OrderTrackingOrderItem) => {
          const open = isLineOpen(item);
          if (!open) {
            const doneLabel =
              item.status === 'CANCELLED'
                ? t('orderTracking.sidebar.receiveLineCancelled')
                : t('orderTracking.sidebar.receiveLineDone');
            return (
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {doneLabel}
              </span>
            );
          }
          return (
            <button
              type="button"
              onClick={() => openLineReceive(item)}
              disabled={receiving}
              title={t('orderTracking.sidebar.receiveLineHint')}
              className="px-2 py-0.5 text-xs font-medium rounded-md border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {t('orderTracking.sidebar.receiveLine')}
            </button>
          );
        },
      },
    ];
    return cols;
  }, [
    t,
    locale,
    showReceivedDate,
    hasLinesWithSo,
    order,
    formatMoney,
    allOpenSelected,
    openLineIds.length,
    receiving,
    selectedLineIds,
    toggleSelectAllOpen,
    toggleLineSelected,
    openLineReceive,
  ]);

  const lineFilterOptions: { value: LineFilter; label: string }[] = [
    { value: 'all', label: t('orderTracking.sidebar.lineFilterAll') },
    { value: 'pending', label: t('orderTracking.sidebar.lineFilterPending') },
    { value: 'late', label: t('orderTracking.sidebar.lineFilterLate') },
  ];

  const canReceiveSelected = selectedLineIds.size > 0 && !receiving;
  const canReceiveOrder = summary.linesPending > 0 && !receiving;
  const selectedOpenItems = items.filter((i) => selectedLineIds.has(i.id) && isLineOpen(i));
  const receiveLineItem =
    receiveMode === 'line' && receiveLineId != null
      ? items.find((i) => i.id === receiveLineId) ?? null
      : null;
  const receiveSingle =
    receiveLineItem ?? (receiveMode === 'selected' && selectedOpenItems.length === 1 ? selectedOpenItems[0] : null);
  const showReceiveQuantity = receiveSingle != null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black transition-opacity duration-300 ease-in-out ${
          isAnimating ? 'opacity-30' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
        aria-hidden
      />
      <aside
        data-order-tracking-sidebar
        className={`fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-gray-200 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out max-md:!w-full md:min-w-[24rem] md:max-w-[96vw] ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: `${widthPct}vw` }}
        role="dialog"
        aria-modal="true"
        aria-label={t('orderTracking.sidebar.title')}
      >
        <button
          type="button"
          aria-label={t('orderTracking.sidebar.resize')}
          title={t('orderTracking.sidebar.resize')}
          onPointerDown={startResize}
          className="absolute inset-y-0 left-0 z-10 hidden w-1.5 cursor-col-resize touch-none bg-transparent hover:bg-sky-400/40 md:block"
        />

        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">{order.orderNumber}</h2>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{order.supplier}</p>
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700">
                {t('orderTracking.sidebar.orderDate')}:{' '}
                <span className="ml-1 font-medium text-gray-900">
                  {formatDisplayDate(order.orderDate, locale)}
                </span>
              </span>
              <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700">
                {t('orderTracking.sidebar.expectedDate')}:{' '}
                <span className="ml-1 font-medium text-gray-900">
                  {formatDisplayDate(order.expectedDate ?? order.effectiveDeadline, locale)}
                </span>
              </span>
              {showReceivedDate && (
                <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700">
                  {t('orderTracking.sidebar.receivedDate')}:{' '}
                  <span className="ml-1 font-medium text-gray-900">
                    {formatDisplayDate(getOrderDisplayReceivedDate(order, items), locale)}
                  </span>
                </span>
              )}
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs border ${getOrderHealthBadgeClasses(order.metrics.healthStatus)}`}
              >
                {getOrderHealthLabel(order.metrics.healthStatus, t)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 shrink-0 transition-colors"
            aria-label={t('orderTracking.sidebar.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 shrink-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiTile
              label={t('orderTracking.sidebar.kpiPendingValue')}
              value={formatMoney(summary.pendingValue)}
              emphasize={summary.pendingValue > 0 ? 'danger' : 'default'}
            />
            <KpiTile
              label={t('orderTracking.sidebar.kpiProgress')}
              value={`${summary.progressUnits}%`}
            />
            <KpiTile
              label={t('orderTracking.sidebar.kpiOpenLines')}
              value={fmt(summary.linesPending)}
              emphasize={summary.linesPending > 0 ? 'danger' : 'default'}
            />
            <KpiTile
              label={t('orderTracking.sidebar.kpiPendingUnits')}
              value={fmt(summary.quantityPending)}
              emphasize={summary.quantityPending > 0 ? 'danger' : 'default'}
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {receiveError && receiveMode == null ? (
            <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {receiveError}
            </p>
          ) : null}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">{t('orderTracking.sidebar.allLines')}</h3>
                <span className="text-xs text-gray-500">
                  {t('orderTracking.sidebar.linesCount', { count: filteredItems.length })}
                </span>
              </div>
              {summary.linesPending > 0 && (
                <p className="mt-1 text-xs text-gray-600 max-w-xl">
                  {t('orderTracking.sidebar.receiveHelp')}
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 self-start sm:self-auto">
              {summary.linesPending > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {selectedLineIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!canReceiveSelected) return;
                        setReceiveError(null);
                        setReceiveLineId(null);
                        setReceiveMode('selected');
                      }}
                      disabled={!canReceiveSelected}
                      title={t('orderTracking.sidebar.markSelectedReceivedHint')}
                      className="px-2.5 py-1 text-xs font-medium rounded-md border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {receiving
                        ? t('orderTracking.sidebar.receiving')
                        : t('orderTracking.sidebar.markSelectedReceived', {
                            count: selectedLineIds.size,
                          })}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (!canReceiveOrder) return;
                      setReceiveError(null);
                      setReceiveLineId(null);
                      setReceiveMode('order');
                    }}
                    disabled={!canReceiveOrder}
                    className="px-2.5 py-1 text-xs font-medium rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('orderTracking.sidebar.markOrderReceivedHint')}
                  >
                    {t('orderTracking.sidebar.markOrderReceived')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleCancelOrder();
                    }}
                    disabled={receiving}
                    className="px-2.5 py-1 text-xs font-medium rounded-md border border-red-200 bg-red-50 text-red-800 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      selectedLineIds.size > 0
                        ? t('orderTracking.sidebar.cancelSelectedHint')
                        : t('orderTracking.sidebar.cancelOrderHint')
                    }
                  >
                    {selectedLineIds.size > 0
                      ? t('orderTracking.sidebar.cancelSelected', { count: selectedLineIds.size })
                      : t('orderTracking.sidebar.cancelOrder')}
                  </button>
                </div>
              )}
              <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50">
                {lineFilterOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setLineFilter(opt.value)}
                    className={`px-2.5 py-1 text-xs font-medium rounded ${
                      lineFilter === opt.value
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {items.length === 0 ? (
            <p className="text-sm text-gray-500">{t('orderTracking.sidebar.noLines')}</p>
          ) : filteredItems.length === 0 ? (
            <p className="text-sm text-gray-500">{t('orderTracking.sidebar.noFilteredLines')}</p>
          ) : (
            <DataTable columns={itemColumns} data={filteredItems} pagination compact />
          )}
        </div>
      </aside>
      <ReceivePurchaseOrderDialog
        open={receiveMode != null}
        title={
          receiveMode === 'order'
            ? t('supply.receivePo.titleOrder')
            : receiveMode === 'line'
              ? t('supply.receivePo.title')
              : t('supply.receivePo.titleSelected')
        }
        busy={receiving}
        error={receiveError}
        showQuantity={showReceiveQuantity}
        orderedQty={receiveSingle?.quantity}
        alreadyReceived={receiveSingle?.receivedQuantity}
        pendingQty={receiveSingle?.pendingQuantity}
        lineCount={receiveMode === 'order' ? summary.linesPending : selectedOpenItems.length}
        onClose={() => {
          if (receiving) return;
          setReceiveMode(null);
          setReceiveLineId(null);
          setReceiveError(null);
        }}
        onConfirm={(payload) => {
          if (receiveMode === 'order') {
            void handleMarkOrderReceived(payload);
            return;
          }
          if (receiveMode === 'line') {
            void handleMarkLineReceived(payload);
            return;
          }
          void handleMarkSelectedReceived(payload);
        }}
      />
    </>
  );
}
